import api from '../api/api';
import { Tag } from '../models/common-types';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { i18n } from '../i18n/i18n';
import { NOTIFICATION, useNotifications } from '@cellebrite/saas-ui-common';

export interface TagToggle {
    tagId: string;
    elementId: string;
}

export interface TagAction {
    id?: string;
    name: string;
    color: string;
}
export default function useTags(caseId: string | null, wsId: number | null, streamId: string) {
    const queryClient = useQueryClient();
    const [showMessage] = useNotifications();
    const isReportPage = window.location.href.includes('report');
    const { data: tags } = useQuery<Array<Tag>>(
        ['tags', wsId],
        ({ queryKey }) => api.tags.fetchTags(queryKey[1] as number),
        { enabled: !!wsId },
    );

    const { data: aggregatedTags } = useQuery<Array<any>>(
        ['aggregatedTags', wsId, caseId, streamId],
        ({ queryKey }) => api.tags.fetchAggregatedTags(queryKey[1] as number, caseId, streamId),
        { enabled: isReportPage && !!wsId && !!caseId && !!streamId },
    );

    const createTag = useMutation(
        (tagsData: { data: TagAction; successCallback: (tags: Array<Tag>) => void; errorCallback: () => void }) =>
            api.tags.createTag(wsId, tagsData.data),
        {
            onSuccess: (data, tagsData) => {
                showMessage(
                    i18n.translate('TAG_CREATION_SUCCESS', { name: tagsData.data.name }),
                    NOTIFICATION.TYPE.SUCCESS,
                );
                queryClient.invalidateQueries('tags');
                queryClient.invalidateQueries('aggregatedTags');
                tagsData.successCallback(data.tags);
            },
            onError: (_res, tagsData) => {
                showMessage(
                    i18n.translate('TAG_CREATION_ERROR', { name: tagsData.data.name }),
                    NOTIFICATION.TYPE.ERROR,
                );
                tagsData.errorCallback();
            },
        },
    );

    const editTag = useMutation(
        (tagsData: { data: TagAction; successCallback: (tags: Array<Tag>) => void; errorCallback: () => void }) =>
            api.tags.editTag(wsId, tagsData.data),
        {
            onSuccess: (data, tagsData) => {
                showMessage(
                    i18n.translate('TAG_EDIT_SUCCESS', { name: tagsData.data.name }),
                    NOTIFICATION.TYPE.SUCCESS,
                );
                queryClient.invalidateQueries('tags');
                queryClient.invalidateQueries('aggregatedTags');
                tagsData.successCallback(data.tags);
            },
            onError: (_res, tagsData) => {
                showMessage(i18n.translate('TAG_EDIT_ERROR', { name: tagsData.data.name }), NOTIFICATION.TYPE.ERROR);
                tagsData.errorCallback();
            },
        },
    );

    const addTag = useMutation(
        (tagsData: { data: TagToggle; successCallback: (tags: Array<Tag>) => void; errorCallback: () => void }) =>
            api.tags.addTag(wsId, caseId, streamId, tagsData.data),
        {
            onSuccess: (data, tagsData) => {
                queryClient.invalidateQueries('aggregatedTags');
                tagsData.successCallback(data.tags);
            },
            onError: (_res, tagsData) => {
                tagsData.errorCallback();
            },
        },
    );

    const removeTag = useMutation(
        (tagsData: { data: TagToggle; successCallback: (tags: Array<Tag>) => any; errorCallback: () => void }) =>
            api.tags.removeTag(wsId, caseId, streamId, tagsData.data),
        {
            onSuccess: (data, tagsData) => {
                queryClient.invalidateQueries('aggregatedTags');
                tagsData.successCallback(data.tags);
            },
            onError: (_res, tagsData) => {
                tagsData.errorCallback();
            },
        },
    );

    return {
        tags,
        aggregatedTags,
        createTag: createTag.mutate,
        editTag: editTag.mutate,
        addTag: addTag.mutate,
        removeTag: removeTag.mutate,
    };
}
